import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useAuthContext } from "../auth/hooks";
import { useImpersonation } from "../impersonation";
import { deleteTagAlias, fetchTagAliases, putTagAlias } from "./api";
import { TagAliasContext } from "./context";

/**
 * Owns the workspace's `tag value → display name` map (issue #1410).
 *
 * A context rather than a prop threaded from each page: tag values render in
 * ten places across two pages and three component levels, and the components in
 * between (the group-by section, the filter panel, the traces view) have no
 * interest in names. This way a render site opts in by reading the hook, and
 * adding an eleventh needs no change here.
 *
 * Fetched once per session, not per render site — the map is small (bounded by
 * how many values a customer chose to name, not by trace volume) and every
 * consumer needs all of it.
 */
export function TagAliasProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuthContext();
  const { isImpersonating, impersonationDomain } = useImpersonation();
  const [aliases, setAliases] = useState<Record<string, string>>({});
  const [isLoaded, setIsLoaded] = useState(false);

  // Refetched when the impersonated domain changes: this provider sits above
  // the route tree that AppRouter remounts on that change, so it would
  // otherwise keep serving the previous workspace's names.
  useEffect(() => {
    if (!isAuthenticated) {
      setAliases({});
      setIsLoaded(false);
      return;
    }
    let cancelled = false;
    fetchTagAliases()
      .then((map) => {
        if (!cancelled) setAliases(map);
      })
      .catch((error) => {
        // A missing name is a cosmetic loss — the raw value still renders and
        // every chart still works. Don't toast the user about it.
        console.error("Failed to load tag display names:", error);
      })
      .finally(() => {
        if (!cancelled) setIsLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, impersonationDomain]);

  const clearName = useCallback(async (value: string) => {
    try {
      await deleteTagAlias(value);
      setAliases((prev) => {
        const next = { ...prev };
        delete next[value];
        return next;
      });
      toast.success("Display name removed");
    } catch (error) {
      toast.error("Failed to remove display name");
      console.error("Tag alias delete error:", error);
    }
  }, []);

  const rename = useCallback(
    async (value: string, displayName: string) => {
      const name = displayName.trim();
      // Clearing the field means "stop naming this", which is a delete — the
      // server rejects a blank name rather than storing an invisible one.
      if (!name) return clearName(value);
      try {
        await putTagAlias(value, name);
        setAliases((prev) => ({ ...prev, [value]: name }));
        toast.success(`Renamed to “${name}”`);
      } catch (error) {
        toast.error("Failed to rename tag value");
        console.error("Tag alias rename error:", error);
      }
    },
    [clearName],
  );

  return (
    <TagAliasContext.Provider
      value={{
        aliases,
        isLoaded,
        canRename: isAuthenticated && !isImpersonating,
        rename,
        clearName,
      }}
    >
      {children}
    </TagAliasContext.Provider>
  );
}
